import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LineReveal, FadeUp } from "../components/Reveal";
import BookButton from "../components/BookButton";
import { buildCalendlyUrl, openCalendlyPopup } from "../lib/calendly";
import { ArrowUpRight, CalendarDays, Clock, Video, MessageSquare } from "lucide-react";

function waitForCalendly(cb, tries = 40) {
  if (typeof window === "undefined") return;
  if (window.Calendly && typeof window.Calendly.initInlineWidget === "function") {
    cb();
    return;
  }
  if (tries <= 0) return;
  setTimeout(() => waitForCalendly(cb, tries - 1), 150);
}

export default function Book() {
  const embedRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!embedRef.current) return;
    // Clear any previous embed instance (route re-mounts, etc.)
    embedRef.current.innerHTML = "";
    waitForCalendly(() => {
      window.Calendly.initInlineWidget({
        url: buildCalendlyUrl(),
        parentElement: embedRef.current,
      });
      setReady(true);
    });
  }, []);

  return (
    <main data-testid="book-page" className="pt-[112px]">
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="rule" /><span className="overline">Chapter 00 — Begin</span>
          </div>
          <h1 className="font-serif font-light tracking-[-0.02em] leading-[0.94] text-[12vw] md:text-[8.5vw]">
            <LineReveal
              lines={[
                <>Book a free</>,
                <>career <em className="italic text-[#C26D5C]">call</em>.</>,
              ]}
              stagger={0.12}
              delay={0.1}
            />
          </h1>
          <FadeUp delay={0.35}>
            <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-[#1A1C1A]/80">
              A 30-minute discovery call. Online, unhurried, and free. Pick a time that works — the calendar below is live.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* Left column — meta */}
            <FadeUp className="col-span-12 md:col-span-4">
              <div className="border border-[#1A1C1A] p-8 md:p-10 h-full flex flex-col justify-between min-h-[520px]">
                <div>
                  <div className="overline text-[#C26D5C] mb-4">Session Details</div>
                  <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight leading-tight">
                    Free Discovery Call
                  </h2>

                  <ul className="mt-8 space-y-5">
                    <li className="flex items-start gap-3">
                      <Clock size={18} strokeWidth={1.5} className="mt-1 text-[#C26D5C]" />
                      <div>
                        <div className="text-sm font-medium">30 minutes</div>
                        <div className="text-xs text-[#1A1C1A]/70">Enough to hear you clearly, without the rush.</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Video size={18} strokeWidth={1.5} className="mt-1 text-[#C26D5C]" />
                      <div>
                        <div className="text-sm font-medium">Online (Zoom / Google Meet)</div>
                        <div className="text-xs text-[#1A1C1A]/70">Link arrives in your inbox on confirmation.</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CalendarDays size={18} strokeWidth={1.5} className="mt-1 text-[#C26D5C]" />
                      <div>
                        <div className="text-sm font-medium">Reschedule anytime</div>
                        <div className="text-xs text-[#1A1C1A]/70">Free rescheduling up to 24 hours in advance.</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <MessageSquare size={18} strokeWidth={1.5} className="mt-1 text-[#C26D5C]" />
                      <div>
                        <div className="text-sm font-medium">Prefer to write first?</div>
                        <div className="text-xs text-[#1A1C1A]/70">
                          <Link to="/contact" className="underline underline-offset-2 hover:text-[#C26D5C]">
                            Send us a note
                          </Link>{" "}
                          and we&rsquo;ll reply within 2 working days.
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="mt-10">
                  <BookButton
                    data-testid="book-popup-cta"
                    className="btn-cf-ghost w-full justify-center"
                  >
                    Open in Popup
                  </BookButton>
                  <a
                    href={buildCalendlyUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="book-newtab-link"
                    className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1A1C1A]/70 hover:text-[#C26D5C] transition-colors"
                  >
                    Or open in a new tab <ArrowUpRight size={12} />
                  </a>
                </div>
              </div>
            </FadeUp>

            {/* Right — live embed */}
            <FadeUp className="col-span-12 md:col-span-8" delay={0.1}>
              <div className="relative border border-[#1A1C1A] bg-[#F4F2F0] overflow-hidden">
                {!ready && (
                  <div
                    data-testid="book-embed-loading"
                    className="absolute inset-0 z-10 flex items-center justify-center bg-[#F4F2F0] pointer-events-none"
                  >
                    <div className="text-center">
                      <div className="overline text-[#1A1C1A]/60 mb-3">Loading calendar</div>
                      <div className="font-serif italic text-2xl">One moment…</div>
                    </div>
                  </div>
                )}
                <div
                  ref={embedRef}
                  data-testid="calendly-inline-embed"
                  className="calendly-inline-widget"
                  style={{ minWidth: "320px", height: "820px" }}
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
